import React from 'react';

import VpnKeyIcon from '@mui/icons-material/VpnKey';
import { Alert, Box, Button, TextField, Typography } from '@mui/material';

import { styles } from '../styles.ts';

interface DynamicPinFormProps {
    onSubmit: (response: string) => Promise<void>;
    error?: string | null;
    isLoading?: boolean;
}

const MIN_LENGTH = 8;
const MAX_LENGTH = 64;

export const DynamicPinForm = ({ onSubmit, error, isLoading }: DynamicPinFormProps) => {
    const [value, setValue] = React.useState('');
    const [localError, setLocalError] = React.useState<string | null>(null);

    React.useEffect(() => {
        if (error) {
            setLocalError(null);
        }
    }, [error]);

    const handleChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        setLocalError(null);
        const input = event.target.value
            .toUpperCase()
            .replace(/[^A-Z0-9]/g, '')
            .slice(0, MAX_LENGTH);
        setValue(input);
    }, []);

    const handleSubmit = React.useCallback(async () => {
        const trimmed = value.trim();

        if (trimmed.length < MIN_LENGTH) {
            setLocalError('Please enter a valid dynamic PIN');
            return;
        }

        setLocalError(null);

        try {
            await onSubmit(trimmed);
        } catch (err) {
            const message = err instanceof Error ? err.message : 'An error occurred. Please try again.';
            setLocalError(message);
        }
    }, [onSubmit, value]);

    const handleKeyDown = React.useCallback(
        (event: React.KeyboardEvent<HTMLInputElement>) => {
            if (event.key === 'Enter') {
                handleSubmit();
            }
        },
        [handleSubmit]
    );

    const isSubmitDisabled = isLoading || value.trim().length < MIN_LENGTH;
    const displayError = error ?? localError;

    return (
        <Box sx={styles.container}>
            <Box sx={styles.iconWrapper}>
                <VpnKeyIcon sx={styles.icon} />
            </Box>

            <Box sx={styles.headerSection}>
                <Typography variant="h6" sx={styles.title}>
                    Enter dynamic PIN
                </Typography>
                <Typography variant="body2" sx={styles.subtitle}>
                    Enter the challenge response code from your authenticator device
                </Typography>
            </Box>

            {displayError && (
                <Alert severity="error" sx={styles.alert}>
                    {displayError}
                </Alert>
            )}

            <TextField
                fullWidth
                type="text"
                placeholder="AAADCWAAAAADIAAAABQQAAAAIMAAAADL"
                value={value}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                disabled={isLoading}
                autoComplete="off"
                inputProps={{
                    maxLength: MAX_LENGTH,
                    'aria-label': 'Dynamic PIN input',
                }}
                sx={styles.challengeInput}
            />

            <Button
                fullWidth
                variant="contained"
                onClick={handleSubmit}
                disabled={isSubmitDisabled}
                sx={styles.submitButton}
            >
                {isLoading ? 'Verifying...' : 'Unlock secret'}
            </Button>
        </Box>
    );
};
